import { useTranslation } from 'react-i18next';
import type { StaffResponse } from '../../api/generated';
import { Tag } from '../UI';
import { LoadError } from '../UI/LoadError';
import { LoadingPlaceholder } from '../UI/LoadingPlaceholder';
import { EMPTY_VALUE } from '../../constants/displayConstants';
import type { CompetenceNameById } from './preferredStaffHelpers';
import { resolveCompetenceNames } from './preferredStaffHelpers';
import styles from './PatientDetailsCard.module.css';

interface PreferredStaffListProps {
  preferredStaff: StaffResponse[];
  // Whether the patient record itself holds preferred-staff ids. The ids come with the
  // patient, so when there are none the field's emptiness is the truth no matter what
  // the roster is doing — only resolving ids to names needs the roster.
  hasStoredPreferredStaff: boolean;
  competenceNameById: CompetenceNameById;
  isStaffLoaded: boolean;
  hasStaffError: boolean;
  onRetryStaff: () => void;
}

export const PreferredStaffList = ({
  preferredStaff,
  hasStoredPreferredStaff,
  competenceNameById,
  isStaffLoaded,
  hasStaffError,
  onRetryStaff
}: PreferredStaffListProps) => {
  const { t } = useTranslation();
  const hasPreferredStaff = preferredStaff.length > 0;
  // An unloaded or failed roster must not read as "no preferred staff" when the patient
  // has some stored — mirror the edit form's gate instead of falling through to "—".
  const isBlocked = hasStoredPreferredStaff && (hasStaffError || !isStaffLoaded);
  const isBlockLayout = hasPreferredStaff || isBlocked;

  const renderValue = (): React.ReactNode => {
    if (isBlocked) {
      return hasStaffError ? (
        <LoadError message={t('preferred-staff-load-error')} onRetry={onRetryStaff} />
      ) : (
        <LoadingPlaceholder />
      );
    }
    if (!hasPreferredStaff) {
      return <span className={styles.fieldValue}>{EMPTY_VALUE}</span>;
    }
    return (
      <ul className={styles.preferredStaffList}>
        {preferredStaff.map((member) => {
          const competences = resolveCompetenceNames(member, competenceNameById);
          return (
            <li key={member.id} className={styles.preferredStaffRow}>
              <span className={styles.preferredStaffName}>{member.fullName}</span>
              {competences.length > 0 && (
                <div className={styles.pills}>
                  {competences.map((name) => (
                    <Tag key={name} label={name} size="small" />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={isBlockLayout ? styles.fieldBlock : styles.field}>
      <span className={isBlockLayout ? styles.fieldLabelSmall : styles.fieldLabel}>
        {t('patient-detail-preferred-staff')}
      </span>
      {renderValue()}
    </div>
  );
};
